import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const MediaUpload = ({ onMediaUpload, uploadedMedia = [] }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e?.dataTransfer?.files);
    handleFileUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    handleFileUpload(files);
  };

  const handleFileUpload = async (files) => {
    const validFiles = files?.filter(file => {
      const isValidType = file?.type?.startsWith('image/') || file?.type?.startsWith('video/');
      const isValidSize = file?.size <= 50 * 1024 * 1024; // 50MB limit
      return isValidType && isValidSize;
    });

    if (validFiles?.length === 0) {
      alert('Please select valid image or video files under 50MB');
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = validFiles?.map(async (file) => {
        // Simulate file upload
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        return {
          id: Date.now() + Math.random(),
          name: file?.name,
          type: file?.type,
          size: file?.size,
          url: URL.createObjectURL(file),
          uploadedAt: new Date()?.toISOString()
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      onMediaUpload(uploadedFiles);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMedia = (mediaId) => {
    const updatedMedia = uploadedMedia?.filter(media => media?.id !== mediaId);
    onMediaUpload(updatedMedia);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getMediaIcon = (type) => {
    if (type?.startsWith('image/')) return 'Image';
    if (type?.startsWith('video/')) return 'Video';
    return 'File';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
        <Icon name="Camera" size={20} className="mr-2 text-primary" />
        Media Evidence
      </h3>
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-clinical ${
          isDragging
            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
            <Icon name="Upload" size={24} className="text-muted-foreground" />
          </div>
          
          <div>
            <p className="text-lg font-medium text-foreground">
              Drop files here or click to upload
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Support for photos and videos up to 50MB each
            </p>
          </div>
          
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              iconName="Camera"
              iconPosition="left"
              onClick={() => fileInputRef?.current?.click()}
              disabled={uploading}
            >
              Choose Files
            </Button>
            
            <Button
              type="button"
              variant="outline"
              iconName="Smartphone"
              iconPosition="left"
              onClick={() => {
                // Simulate mobile camera capture
                fileInputRef?.current?.click();
              }}
              disabled={uploading}
            >
              Take Photo
            </Button>
          </div>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      {/* Upload Progress */}
      {uploading && (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="animate-spin">
              <Icon name="Loader2" size={20} className="text-primary" />
            </div>
            <span className="text-sm text-muted-foreground">Uploading files...</span>
          </div>
        </div>
      )}
      {/* Uploaded Media Grid */}
      {uploadedMedia?.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-foreground mb-3">
            Uploaded Media ({uploadedMedia?.length})
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {uploadedMedia?.map((media) => (
              <div key={media?.id} className="relative group">
                <div className="aspect-square bg-muted rounded-lg overflow-hidden">
                  {media?.type?.startsWith('image/') ? (
                    <Image
                      src={media?.url}
                      alt={media?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center">
                      <Icon name={getMediaIcon(media?.type)} size={32} className="text-muted-foreground mb-2" />
                      <span className="text-xs text-muted-foreground text-center px-2">
                        {media?.name}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Media Info Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-clinical rounded-lg flex flex-col justify-between p-2">
                  <div className="flex justify-end">
                    <button
                      onClick={() => handleRemoveMedia(media?.id)}
                      className="w-6 h-6 bg-error rounded-full flex items-center justify-center hover:bg-error/80 transition-clinical"
                    >
                      <Icon name="X" size={14} color="white" />
                    </button>
                  </div>
                  
                  <div className="text-white">
                    <p className="text-xs font-medium truncate">{media?.name}</p>
                    <p className="text-xs opacity-80">{formatFileSize(media?.size)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Upload Guidelines */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h4 className="text-sm font-medium text-foreground mb-2 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-primary" />
          Upload Guidelines
        </h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Supported formats: JPG, PNG, GIF, MP4, MOV, AVI</li>
          <li>• Maximum file size: 50MB per file</li>
          <li>• Include photos of the incident scene, damage, or hazards</li>
          <li>• Videos should show the sequence of events if possible</li>
          <li>• Ensure faces are visible if personnel identification is needed</li>
        </ul>
      </div>
    </div>
  );
};

export default MediaUpload;